import React, { useMemo, useState } from 'react';
import Section from './Section';
import ThreadList from '../../components/forms/outreach/ThreadList';
import ThreadChat from '../../components/forms/outreach/ThreadChat';
import ThreadStatusPill from '../../components/forms/outreach/ThreadStatusPill';
import OutreachMovesGallery from './OutreachMovesGallery';
import { OutreachAction, SupplierThreadStatus } from '../../components/forms/outreach/types';
import { actionsForThread, buildActionIndex, composeAskQuery } from '../../hooks/outreachActionModel';
import { useStableThreadOrder } from '../../hooks/useStableThreadOrder';
import { ACTIONS, AWAITING_KICKOFF_THREAD, THREADS, afterSendingTo } from './outreachFixtures';
import { WizardAccessProvider } from '../../contexts/WizardAccessContext';
import { ViewOnlyBanner } from '../../components/ui';

const THREAD_STATUSES: SupplierThreadStatus[] = ['not_sent', 'awaiting_reply', 'negotiating', 'replied', 'failed'];

/**
 * /design gallery for the Step 4 outreach surface — sample data to eyeball the
 * action-sorted rows with Robbie's read line, the status pills, the change
 * ribbon, the counter card, the query bubble with its sent-email peek, the
 * accumulating staging, the in-flight send and the three reads of an empty
 * thread against `design/step4-outreach.dc.html` (the live step needs auth
 * + real sends).
 *
 * The mock's state strip is not here because it is no longer on the step: its
 * counts restated the thread list less precisely than the list itself does.
 * Neither is the kickoff plan — the step lands in the conversations now, and
 * an un-contacted supplier's opening asks live in their own thread.
 *
 * The view-only panes are the same surface seen by a colleague who did not
 * create the RFQ: everything readable, every control gone.
 */

const noop = () => undefined;
const EMPTY_STAGING: string[] = [];
const NO_ACTIONS: OutreachAction[] = [];

/** One thread's composer: what is typed in it, and which asks Robbie put there. */
interface OutreachBox {
  text: string;
  stagedIds: string[];
}

const EMPTY_BOX: OutreachBox = { text: '', stagedIds: EMPTY_STAGING };

const OutreachGallery: React.FC = () => {
  const [activeId, setActiveId] = useState('t-kaisen');
  // Mirrors the live step: every thread keeps its own box — the typed query and
  // the ids whose asks are sitting in it — so switching rows swaps both.
  const [boxes, setBoxes] = useState<Record<string, OutreachBox>>({});
  // Stands in for a send landing on Auriga: its action closes and its status
  // moves on, so live sorting would drop the row. The order must not budge.
  const [sentToAuriga, setSentToAuriga] = useState(false);

  const moved = useMemo(() => (sentToAuriga ? afterSendingTo('Auriga Drivetrain') : null), [sentToAuriga]);
  const index = useMemo(() => buildActionIndex(moved?.actions ?? ACTIONS), [moved]);
  const threads = useStableThreadOrder(moved?.threads ?? THREADS, index);

  const active = threads.find((t) => t.id === activeId) ?? threads[0];
  const kaisen = threads.find((t) => t.id === 't-kaisen') ?? threads[0];
  const auriga = threads.find((t) => t.id === 't-auriga') ?? threads[0];

  const box = boxes[active.id] ?? EMPTY_BOX;
  const putInBox = (next: OutreachBox) => setBoxes((prev) => ({ ...prev, [active.id]: next }));

  const stageIntoComposer = (chosen: OutreachAction[], approvedPrice?: number) => {
    const fresh = chosen.filter((action) => !box.stagedIds.includes(action.id));
    if (fresh.length === 0) return;
    const query = composeAskQuery(active, fresh, approvedPrice, box.stagedIds.length);
    putInBox({
      text: box.text.trim() ? `${box.text.trimEnd()}\n\n${query}` : query,
      stagedIds: [...box.stagedIds, ...fresh.map((action) => action.id)],
    });
  };

  // Emptying the box empties the staging with it, exactly as the live step does.
  const editComposer = (text: string) => putInBox({ text, stagedIds: text.trim() ? box.stagedIds : [] });

  return (
    <Section title="Wizard step 4 — outreach surface">
      <div className="flex flex-col gap-s5">
        <div className="flex flex-wrap items-center gap-s3">
          {THREAD_STATUSES.map((status) => (
            <ThreadStatusPill key={status} status={status} />
          ))}
          <span className="text-meta text-ink-muted">
            Every state a conversation can be in, in the buyer&rsquo;s words. The buyer drafts nothing here — they send
            an ask and Robbie writes the email — so a supplier nobody has written to is simply not sent.
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-s3">
          <button
            type="button"
            onClick={() => setSentToAuriga((prev) => !prev)}
            className="inline-flex shrink-0 items-center rounded-control border border-action-border bg-surface px-[11px] py-[5px] text-meta font-semibold text-action-deep hover:bg-action-bg"
          >
            {sentToAuriga ? 'Rewind Auriga to unsent' : 'Simulate: the ask to Auriga lands'}
          </button>
          <span className="text-meta text-ink-muted">
            Auriga&rsquo;s pill, read line and count all change — and its row stays exactly where it was. Under live
            sorting it would drop to the bottom while the buyer was reading it.
          </span>
        </div>

        <div className="grid h-[620px] grid-cols-[minmax(150px,268px)_minmax(0,1fr)] overflow-hidden rounded-card border border-line bg-surface">
          <ThreadList threads={threads} activeThreadId={activeId} actionIndex={index} onSelectThread={setActiveId} />
          <ThreadChat
            activeThread={active}
            actions={actionsForThread(index, active)}
            messageInput={box.text}
            onMessageInputChange={editComposer}
            onSendMessage={noop}
            onStageActions={stageIntoComposer}
            stagedActionIds={box.stagedIds}
            onDismissAction={noop}
            isSending={false}
            sendError={null}
            onDismissSendError={noop}
            onSaveContactEmail={async () => false}
            isSavingEmail={false}
            emailSaveError={null}
            onDiscardThread={noop}
            isDiscardingThread={false}
          />
        </div>

        <OutreachMovesGallery />

        <WizardAccessProvider readOnly ownerName="Dana Okafor">
          <div className="flex flex-col gap-s4">
            <ViewOnlyBanner ownerName="Dana Okafor" />
            <div className="grid h-[620px] grid-cols-[minmax(150px,268px)_minmax(0,1fr)] overflow-hidden rounded-card border border-line bg-surface">
              <ThreadList threads={threads} activeThreadId={kaisen.id} actionIndex={index} onSelectThread={noop} />
              <ThreadChat
                activeThread={kaisen}
                actions={index.byThreadId.get(kaisen.id) ?? []}
                messageInput=""
                onMessageInputChange={noop}
                onSendMessage={noop}
                onStageActions={noop}
                stagedActionIds={EMPTY_STAGING}
                onDismissAction={noop}
                isSending={false}
                sendError={null}
                onDismissSendError={noop}
                onSaveContactEmail={async () => false}
                isSavingEmail={false}
                emailSaveError={null}
                onDiscardThread={noop}
                isDiscardingThread={false}
              />
            </div>
            <div className="h-[460px] overflow-hidden rounded-card border border-line bg-surface">
              <ThreadChat
                activeThread={auriga}
                actions={actionsForThread(index, auriga)}
                messageInput=""
                onMessageInputChange={noop}
                onSendMessage={noop}
                onStageActions={noop}
                stagedActionIds={EMPTY_STAGING}
                onDismissAction={noop}
                isSending={false}
                sendError={null}
                onDismissSendError={noop}
                onSaveContactEmail={async () => false}
                isSavingEmail={false}
                emailSaveError={null}
                onDiscardThread={noop}
                isDiscardingThread={false}
              />
            </div>
          </div>
        </WizardAccessProvider>

        <p className="m-0 text-meta text-ink-muted">
          View-only, the two panes above: a colleague reads the whole conversation, the quote read-out, the change
          ribbon and Robbie&rsquo;s moves — the moves bar keeps its count and its sheet keeps the reasoning, because
          both are things worth knowing. What goes is everything that would write: no composer, no staging, no remove
          supplier, no contact-email fix, no tick boxes and no batch send. An un-contacted supplier&rsquo;s thread
          still says what Robbie would open with — the ask cards keep their reasoning and drop their controls.
        </p>

        <div className="flex h-[760px] overflow-hidden rounded-card border border-line bg-surface">
          <ThreadChat
            activeThread={kaisen}
            actions={index.byThreadId.get('t-kaisen') ?? []}
            messageInput="Can you hold ₹1,060 if we confirm by Friday?"
            onMessageInputChange={noop}
            onSendMessage={noop}
            onStageActions={noop}
            stagedActionIds={EMPTY_STAGING}
            onDismissAction={noop}
            isSending
            sendError={null}
            onDismissSendError={noop}
            onSaveContactEmail={async () => false}
            isSavingEmail={false}
            emailSaveError={null}
            onDiscardThread={noop}
            isDiscardingThread={false}
          />
        </div>

        <div className="grid h-[440px] grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-s4">
          <div className="overflow-hidden rounded-card border border-line bg-surface">
            <ThreadChat
              activeThread={AWAITING_KICKOFF_THREAD}
              actions={NO_ACTIONS}
              kickoffPending
              messageInput=""
              onMessageInputChange={noop}
              onSendMessage={noop}
              onStageActions={noop}
              stagedActionIds={EMPTY_STAGING}
              onDismissAction={noop}
              isSending={false}
              sendError={null}
              onDismissSendError={noop}
              onSaveContactEmail={async () => false}
              isSavingEmail={false}
              emailSaveError={null}
              onDiscardThread={noop}
              isDiscardingThread={false}
            />
          </div>
          <div className="overflow-hidden rounded-card border border-line bg-surface">
            <ThreadChat
              activeThread={AWAITING_KICKOFF_THREAD}
              actions={NO_ACTIONS}
              kickoffPending={false}
              messageInput=""
              onMessageInputChange={noop}
              onSendMessage={noop}
              onStageActions={noop}
              stagedActionIds={EMPTY_STAGING}
              onDismissAction={noop}
              isSending={false}
              sendError={null}
              onDismissSendError={noop}
              onSaveContactEmail={async () => false}
              isSavingEmail={false}
              emailSaveError={null}
              onDiscardThread={noop}
              isDiscardingThread={false}
            />
          </div>
        </div>

        <p className="m-0 text-meta text-ink-muted">
          An empty thread spends its space on the asks instead of on a centred shrug: select Shenzhen Kinetix or Auriga
          Drivetrain in the first pane and the opening asks fill the body as full cards — title, reasoning, &ldquo;Put in
          the box&rdquo; — with the composer under them and no moves bar, because the bar exists to protect a
          conversation that is not there yet. Auriga carries two, so the stack reads as a stack. The first message flips
          the pane back to the compact bar for good.
        </p>

        <p className="m-0 text-meta text-ink-muted">
          The two panes above are the same supplier with no asks, and the difference between them is the server&rsquo;s
          word. Left: `kickoffPending` is true, so Robbie says he is working it out and the skeletons say how much is
          coming. Right: the run has finished and produced nothing, so the loader is gone and the line says so and
          points at the composer. There is no third state where a spinner outlives the run that started it.
        </p>

        <p className="m-0 text-meta text-ink-muted">
          Thread order is the point of the first pane: the two suppliers nobody has written to yet sit at the top,
          because their opening ask is something to send right now, above the sent threads that can only be waited on.
          A follow-up the server is holding back never arrives, so it cannot push a waiting thread up. Kaisen carries
          four moves on one bar, the change ribbon under its reply inside the scroll, and a query bubble whose
          &ldquo;view the email Robbie sent&rdquo; peek opens the email read-only; Sundara&rsquo;s bubble predates
          queries, so it stays the email itself. The second thread pane is a send in flight — spinner in the composer,
          &ldquo;Robbie is writing to Kaisen Motion Systems…&rdquo; on the bar, and every move held still.
        </p>

        <p className="m-0 text-meta text-ink-muted">
          Staging is live in the first pane, and it is the ONLY thing a pill does — nothing on this bar reaches a
          supplier. Tap one of Kaisen&rsquo;s pills: its ask lands in the composer with the cursor at the end, under
          anything already typed, and the pill stays put, subdued, saying it is in your box. Tap a second and both asks
          are in the box, numbered as one running list. The bundle button then reads &ldquo;Put the other N in&rdquo;
          and its count has dropped to the asks still out — click it and it adds only those, never a duplicate.
        </p>

        <p className="m-0 text-meta text-ink-muted">
          The counter takes two steps on purpose: &ldquo;Confirm ₹X&rdquo; locks the number, and only then does
          &ldquo;Accept &amp; put in the box&rdquo; enable — an unconfirmed price is never written into a query, by that
          step or by the bundle. Editing the number un-confirms it. Nothing here closes a move: only the composer&rsquo;s
          own send marks every ask it carried done, and clearing the box empties the staging with it. Every thread owns
          its box — stage on Kaisen, click another row, and the composer and the tags are that thread&rsquo;s; come back
          and Kaisen&rsquo;s asks are still sitting there, unsent. There is no direct send anywhere on this step: an
          opening ask goes through the same box and the same button as everything else.
        </p>

        <p className="m-0 text-meta text-ink-muted">
          Row order is a snapshot taken when the step opens, not a live sort. Use the button above the first pane to
          land the ask on Auriga: the pill, the read line and the count all update in place, and no row moves. Coming
          back to the step re-sorts from scratch.
        </p>
      </div>
    </Section>
  );
};

export default OutreachGallery;
